import {
  ArrowRightIcon,
  ArrowsClockwiseIcon,
  DotsThreeIcon,
  SidebarSimpleIcon,
  XCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ScheduleConfirmDialog } from './workflow-tracker/ScheduleConfirmDialog';
import { WorkflowFailureBanner } from './workflow-tracker/WorkflowFailureBanner';
import { type WorkflowStepRenderState, WorkflowStepRow } from './workflow-tracker/WorkflowStepRow';
import { refreshAfterWorkflowCancel, searchWithoutPlan } from './workflow-tracker/cancelWorkflow';

import { PlanContextSidebar } from '@/components/plans/PlanContextSidebar';
import { WorkflowLibraryDialog } from '@/components/plans/WorkflowLibraryDialog';
import { Button } from '@/components/ui/button';
import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { Confirm } from '@/components/ui/confirm';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useInspector } from '@/components/ui/inspector/hooks/useInspector';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/use-auth';
import {
  ITEM_META,
  MENU_ITEM_MOBILE,
  MOBILE_ICON_BUTTON_XS,
  PANEL_HEADER_SHELL,
  PANEL_HEADER_TITLE,
} from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import {
  AdvanceWorkflowStepError,
  advanceWorkflowStep,
} from '@/lib/workflows/advance-workflow-step';
import { campaignService } from '@/services/data/CampaignService';
import type {
  WorkflowState,
  WorkflowStep,
  WorkflowTemplate,
} from '@/services/data/CampaignService';
import { openDock } from '@/stores/_panel-mutex';

interface WorkflowTrackerBodyProps {
  projectId: string;
  onClose: () => void;
  /**
   * Dual-mounted: the modal Inspector (default 'modal' → X close) and the docked
   * ArtifactDrawer rail ('dock' → SidebarSimpleIcon collapse glyph). The drawer
   * opts out of its own DrawerCloseAffordance in workflow mode, so this body owns
   * the dismiss — and in the rail it must read as "collapse", not "close".
   */
  variant?: 'modal' | 'dock';
}

function resolveStepState(step: WorkflowStep, state: WorkflowState): WorkflowStepRenderState {
  const inHistory = state.history.find((h) => h.step_id === step.step_id);
  if (inHistory && inHistory.status === 'completed') return 'completed';
  if (state.current_step_id === step.step_id) {
    if (state.status === 'failed') return 'failed';
    if (state.status === 'awaiting_approval') return 'awaiting_approval';
    if (state.status === 'running') return 'running';
    if (state.status === 'completed') return 'completed';
  }
  return 'up_next';
}

function findStepFailureReason(step: WorkflowStep, state: WorkflowState): string | null {
  if (state.current_step_id !== step.step_id || state.status !== 'failed') return null;
  const lastFailed = [...state.history]
    .reverse()
    .find((h) => h.step_id === step.step_id && (h as Record<string, unknown>).status === 'failed');
  if (!lastFailed) return null;
  const reason = (lastFailed as Record<string, unknown>).failure_reason;
  return typeof reason === 'string' ? reason : null;
}

function findStepCompletedAt(step: WorkflowStep, state: WorkflowState): string | null {
  const entry = [...state.history].reverse().find((h) => h.step_id === step.step_id);
  if (!entry) return null;
  const ts = (entry as Record<string, unknown>).completed_at;
  return typeof ts === 'string' ? ts : null;
}

function findStepArtifact(
  step: WorkflowStep,
  state: WorkflowState,
): { url?: string; kind?: string; note?: string; postId?: string } | null {
  const asPostId = (v: unknown): string | undefined =>
    typeof v === 'string' && v.length > 0 ? v : undefined;
  const historyEntry = [...state.history].reverse().find((h) => h.step_id === step.step_id);
  const artifacts = historyEntry?.artifacts;
  if (artifacts && artifacts.length > 0) {
    const a = artifacts[0] as Record<string, unknown>;
    return {
      url: a.url as string | undefined,
      kind: a.kind as string | undefined,
      note: a.note as string | undefined,
      // The agent stamps the social_posts.id as `id` (advance_workflow_step
      // artifacts schema) so the tracker can open the editable post, not a raw
      // storage URL. Only treat it as a post id for post-kind artifacts.
      postId: (a.kind as string | undefined) === 'post' ? asPostId(a.id ?? a.postId) : undefined,
    };
  }
  if (state.current_step_id === step.step_id) {
    const pending = state.pending_artifact as
      { url?: string; kind?: string; note?: string; id?: string; postId?: string } | undefined;
    if (pending?.url) {
      return {
        url: pending.url,
        kind: pending.kind,
        note: pending.note,
        postId: pending.kind === 'post' ? asPostId(pending.id ?? pending.postId) : undefined,
      };
    }
  }
  return null;
}

function StepArtifactPreview({
  artifact,
}: {
  artifact: { url?: string; kind?: string; note?: string; postId?: string };
}) {
  // When the artifact is a saved post, clicking opens the EDITABLE post in the
  // inspector (pushed onto the stack so the tracker is preserved beneath and
  // pop returns to it) instead of a raw storage URL that 404s or shows a bare
  // image. Falls back to the static preview / link when no post id is present.
  const pushSubject = useInspector((s) => s.push);
  // A broken/404 artifact image drops to the static open-post / link fallback
  // (exactly the "static preview / link" the comment above describes) rather
  // than rendering the browser's broken-`<img>` glyph inside the preview box.
  const [imgFailed, setImgFailed] = useState(false);
  if (!artifact.url) return null;
  const kind = (artifact.kind ?? '').toLowerCase();
  const postId = artifact.postId;
  const openPost = postId ? () => pushSubject({ kind: 'post', postId }) : undefined;
  const isImage = kind === 'image' || /\.(png|jpe?g|webp|gif)(\?|$)/i.test(artifact.url);

  const media =
    kind === 'video' ? (
      <video
        src={artifact.url}
        className="block max-h-[280px] w-auto"
        controls
        playsInline
        preload="metadata"
      />
    ) : isImage && !imgFailed ? (
      <img
        src={artifact.url}
        alt={artifact.note ?? 'Step artifact'}
        onError={() => setImgFailed(true)}
        className="block max-h-[280px] w-auto h-auto object-contain"
      />
    ) : null;

  if (media) {
    if (openPost) {
      return (
        <button
          type="button"
          onClick={openPost}
          aria-label="Open post"
          className="group/artifact block w-full max-w-full overflow-hidden rounded-md border border-border/40 bg-muted/30 text-left transition-colors hover:border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {media}
          <span className="flex items-center justify-end gap-1 px-2 py-1 text-detail text-muted-foreground group-hover/artifact:text-foreground">
            Open post →
          </span>
        </button>
      );
    }
    return (
      <div className="inline-block max-w-full overflow-hidden rounded-md border border-border/40 bg-muted/30">
        {media}
      </div>
    );
  }

  if (openPost) {
    return (
      <button
        type="button"
        onClick={openPost}
        className="block truncate-fade text-left text-detail text-foreground/70 underline-offset-2 hover:underline"
      >
        {artifact.note ?? 'Open post'}
      </button>
    );
  }
  return (
    <a
      href={artifact.url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-detail text-foreground/70 underline-offset-2 hover:underline truncate block"
    >
      {artifact.note ?? artifact.url}
    </a>
  );
}

/**
 * Workflow tracker — the right-rail body when `baseMode === 'workflow'`.
 *
 * P3 rewrite per §9 of the chat+workflow implementation plan:
 *   - WorkflowBriefSection above step 1 when status='not_started' and brief
 *     is incomplete; gates the Start CTA.
 *   - WorkflowStepRow per step: status disc by SHAPE (a11y F20), uses/creates
 *     metadata, action buttons only on the active row.
 *   - WorkflowFailureBanner on a failed step, fires retry.
 *   - ScheduleConfirmDialog (F12) when the active step's tools include
 *     schedule_content: Approve → dialog → Schedule confirms the approval.
 *   - Footer: small ITEM_META "Working in {Project name} →" link.
 */
export const WorkflowTrackerBody: React.FC<WorkflowTrackerBodyProps> = ({
  projectId,
  onClose,
  variant = 'modal',
}) => {
  const queryClient = useQueryClient();
  const { data: projectResp, isLoading: projectLoading } = useQuery({
    queryKey: ['campaign', projectId],
    queryFn: () => campaignService.getCampaign(projectId),
    refetchInterval: 3000,
  });

  const project = projectResp?.data ?? null;
  const completionToastId = `workflow-complete-${projectId}`;
  const templateId = project?.activeWorkflowTemplateId ?? null;
  const workflowState = project?.workflowState ?? null;
  const [workflowLibraryOpen, setWorkflowLibraryOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelRunning, setCancelRunning] = useState(false);
  const filteredAssets = useMemo(() => [], []);

  const { data: templateResp, isLoading: templateLoading } = useQuery({
    queryKey: ['workflow_template', templateId],
    queryFn: () => (templateId ? campaignService.getWorkflowTemplate(templateId) : null),
    enabled: !!templateId,
    staleTime: 60_000,
  });

  const template = (templateResp?.data ?? null) as WorkflowTemplate | null;
  const steps: WorkflowStep[] = useMemo(() => template?.steps ?? [], [template]);

  const currentIndex = useMemo(() => {
    if (!workflowState || steps.length === 0) return 0;
    const idx = steps.findIndex((s) => s.step_id === workflowState.current_step_id);
    return idx >= 0 ? idx : 0;
  }, [workflowState, steps]);

  const activeStep = useMemo(
    () =>
      workflowState
        ? (steps.find((s) => s.step_id === workflowState.current_step_id) ?? null)
        : null,
    [workflowState, steps],
  );

  const activeStepSchedules = useMemo(
    () => activeStep?.tools?.includes('schedule_content') ?? false,
    [activeStep],
  );

  // Only steps that actually (re)generate an artifact can act on "what should
  // change?" feedback — hide the input on pure confirm/schedule steps so the
  // placeholder never lies.
  const activeStepCanRegenerate = useMemo(
    () =>
      activeStep?.tools?.some(
        (t) => t === 'generate_content' || t === 'generate_image' || t === 'generate_video',
      ) ?? false,
    [activeStep],
  );

  // The Comment Reply Drafts workflow stages replies into the Engagement Hub
  // (never auto-sends) — when it completes, point the user there to Send.
  const isEngagementWorkflow = useMemo(
    () => steps.some((s) => s.tools?.includes('stage_engagement_replies')),
    [steps],
  );

  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [regenFeedback, setRegenFeedback] = useState('');

  // headerStatus removed — the rail header no longer shows a status
  // pill (Jobs/Rams subtraction: the spinner / disc shape on the
  // active step row already communicates state). Memo dropped along
  // with its sole consumer.

  // UXA-053: the optimistic `running` status written below is overwritten
  // ~3s later by refetchInterval while the DB still reads 'awaiting_approval'
  // (the agent hasn't transitioned the step yet) — which re-enables the footer
  // Approve/Regenerate buttons mid-flight and allows a double-Approve. This
  // local guard holds the footer actions disabled from the moment an action is
  // dispatched until the server state actually transitions away from the value
  // it was dispatched from, or a short safety timeout elapses (so a no-op
  // dispatch can never permanently lock the footer).
  const { session } = useAuth();
  const [isDispatching, setIsDispatching] = useState(false);
  const dispatchedFromStatusRef = useRef<WorkflowState['status'] | null>(null);
  const dispatchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trackerMountedRef = useRef(true);
  const trackerProjectIdRef = useRef(projectId);

  // A completion request can resolve after the user leaves the workflow
  // surface. Sonner is app-global, so without cleanup this success toast can
  // appear over an unrelated dialog on Projects and look like that dialog
  // completed a workflow. Tie the notification to this tracker lifetime.
  useEffect(() => {
    trackerMountedRef.current = true;
    trackerProjectIdRef.current = projectId;
    return () => {
      trackerMountedRef.current = false;
      trackerProjectIdRef.current = '';
      toast.dismiss(completionToastId);
    };
  }, [completionToastId, projectId]);

  const clearDispatchGuard = useCallback(() => {
    if (dispatchTimeoutRef.current) {
      clearTimeout(dispatchTimeoutRef.current);
      dispatchTimeoutRef.current = null;
    }
    dispatchedFromStatusRef.current = null;
    setIsDispatching(false);
  }, []);

  // Release the guard once the server state genuinely moves off the status we
  // dispatched from (e.g. awaiting_approval → running/completed/failed). The
  // refetch flap (DB still on the original status) keeps it held.
  useEffect(() => {
    if (!isDispatching) return;
    const current = workflowState?.status ?? null;
    if (current !== null && current !== dispatchedFromStatusRef.current) {
      clearDispatchGuard();
    }
  }, [isDispatching, workflowState?.status, clearDispatchGuard]);

  // Tidy up the safety timeout on unmount.
  useEffect(
    () => () => {
      if (dispatchTimeoutRef.current) clearTimeout(dispatchTimeoutRef.current);
    },
    [],
  );

  const dispatchWorkflowAction = useCallback(
    (action: 'approve' | 'regenerate' | 'retry' | 'start', extra?: Record<string, unknown>) => {
      if (!workflowState) return;
      // Guard against a re-enable flash / double dispatch while the agent
      // transitions the step. Only the footer-driven actions need it.
      if (action === 'approve' || action === 'regenerate' || action === 'retry') {
        dispatchedFromStatusRef.current = workflowState.status;
        setIsDispatching(true);
        if (dispatchTimeoutRef.current) clearTimeout(dispatchTimeoutRef.current);
        dispatchTimeoutRef.current = setTimeout(() => {
          clearDispatchGuard();
        }, 15_000);
      }
      // No optimistic status flip (UXA-053 / regen path): writing status='running'
      // here faked a spinner AND, because 'running' !== dispatchedFrom, tripped
      // the guard-release effect into re-enabling the footer mid-flight (a lying
      // spinner + double-dispatch window). The isDispatching guard alone holds
      // the footer disabled until the REAL server transition lands (or the 15s
      // safety timeout) — the same honest contract approveViaServer uses.
      window.dispatchEvent(
        new CustomEvent('mora:workflowAction', {
          detail: {
            action,
            stepId: workflowState.current_step_id,
            projectId,
            ...(extra ?? {}),
            // Ownership tag — see useCreateChatWorkflowSync's matching guard.
            // This body only ever renders on the page's own rail (ArtifactDrawer
            // / Inspector), never inside the floating panel, so every dispatch
            // from here is 'page'. Placed after the `extra` spread so a caller
            // can never accidentally shadow it.
            source: 'page',
          },
        }),
      );
    },
    [projectId, workflowState, clearDispatchGuard],
  );

  // Server-authoritative approve (red-team final design). Approve no longer
  // depends on CreativeChat being mounted to hear a window event — it calls the
  // durable /api/workflow/advance endpoint directly, which CAS-advances/completes
  // workflow_state and (at the final step) refuses to "complete" a workflow whose
  // posts aren't scheduled. No optimistic 'running' fake: the footer guard is held
  // until the server actually responds, so a lost click can never lie about
  // progress. For a non-final advance we still emit the chat event so the agent
  // (when mounted) generates the next step; the endpoint already moved the state.
  const approveViaServer = useCallback(async () => {
    if (!workflowState) return;
    const fromStepId = workflowState.current_step_id;
    dispatchedFromStatusRef.current = workflowState.status;
    setIsDispatching(true);
    if (dispatchTimeoutRef.current) clearTimeout(dispatchTimeoutRef.current);
    dispatchTimeoutRef.current = setTimeout(() => clearDispatchGuard(), 15_000);
    // Transport extracted to `advanceWorkflowStep` (task 3 of the
    // workflow-panel-run-contract plan) so the panel's inline approval row
    // calls the exact same durable endpoint — behavior below is unchanged.
    try {
      const outcome = await advanceWorkflowStep({
        projectId,
        fromStepId,
        action: 'approve',
        accessToken: session?.access_token,
      });
      if (outcome.result === 'needs_schedule') {
        toast.error(outcome.message);
        clearDispatchGuard();
        return;
      }
      if (
        outcome.result === 'completed' &&
        trackerMountedRef.current &&
        trackerProjectIdRef.current === projectId
      ) {
        toast.success('Workflow completed', {
          id: completionToastId,
          description: 'Your posts are scheduled.',
        });
      } else if (outcome.result === 'advanced') {
        // Non-final: hand off to the agent (when mounted) to generate the next step.
        window.dispatchEvent(
          new CustomEvent('mora:workflowAction', {
            // source: 'page' — see useCreateChatWorkflowSync's ownership guard.
            detail: { action: 'approve', stepId: fromStepId, projectId, source: 'page' },
          }),
        );
      }
      queryClient.invalidateQueries({ queryKey: ['campaign', projectId] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['slot-posts'] });
      queryClient.invalidateQueries({ queryKey: ['planSlots'] });
      clearDispatchGuard();
    } catch (err) {
      toast.error(
        err instanceof AdvanceWorkflowStepError
          ? err.message
          : 'Could not reach the server. Try again.',
      );
      clearDispatchGuard();
    }
  }, [
    workflowState,
    projectId,
    session?.access_token,
    queryClient,
    clearDispatchGuard,
    completionToastId,
  ]);

  const handleApprove = useCallback(() => {
    if (!workflowState) return;
    if (activeStepSchedules) {
      setScheduleDialogOpen(true);
      return;
    }
    const pending = workflowState.pending_artifact as
      { url?: string; kind?: string; note?: string } | undefined;
    if (pending?.url) {
      window.dispatchEvent(
        new CustomEvent('mora:attachToChat', {
          detail: { url: pending.url, kind: pending.kind, note: pending.note },
        }),
      );
    }
    void approveViaServer();
  }, [activeStepSchedules, approveViaServer, workflowState]);

  const handleScheduleConfirm = useCallback(() => {
    setScheduleDialogOpen(false);
    void approveViaServer();
  }, [approveViaServer]);

  const handleRegenerate = useCallback(() => {
    const trimmed = regenFeedback.trim();
    dispatchWorkflowAction('regenerate', trimmed ? { feedback: trimmed } : undefined);
    setRegenFeedback('');
  }, [dispatchWorkflowAction, regenFeedback]);

  const handleRetry = useCallback(() => {
    dispatchWorkflowAction('retry');
  }, [dispatchWorkflowAction]);

  const navigate = useNavigate();
  const location = useLocation();
  const onChatRoute = location.pathname === '/' || location.pathname === '/create';
  const handleStartWorkflow = useCallback(() => {
    if (onChatRoute) {
      window.dispatchEvent(
        new CustomEvent('mora:workflowAction', {
          // source: 'page' — see useCreateChatWorkflowSync's ownership guard.
          detail: {
            action: 'start',
            stepId: workflowState?.current_step_id ?? 1,
            projectId,
            source: 'page',
          },
        }),
      );
      return;
    }
    navigate(`/?planId=${projectId}&workflow=start`);
  }, [onChatRoute, navigate, projectId, workflowState?.current_step_id]);

  const goToProject = useCallback(() => {
    navigate(`/projects/${projectId}`);
  }, [navigate, projectId]);

  // Permanent escape hatch — clears the workflow assignment from the
  // project. Project itself is preserved. Used by the right-rail menu's
  // "Cancel workflow" option so the user always has a single-click exit
  // even when the agent forgets to surface a cancel chip in chat.
  const handleCancelWorkflow = useCallback(async () => {
    if (!projectId) return;
    setCancelRunning(true);
    try {
      const res = await campaignService.unassignWorkflow(projectId);
      if (res.error) throw new Error(res.error.message);
      refreshAfterWorkflowCancel(queryClient, projectId);
      toast.success('Workflow cancelled. Project kept.');
      setCancelConfirmOpen(false);
      // Close the inspector body — the workflow is gone, no point
      // staying on the tracker view.
      onClose();
      if (onChatRoute) {
        const search = searchWithoutPlan(location.search);
        if (search !== null) {
          navigate({ pathname: location.pathname, search }, { replace: true });
        }
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not cancel workflow');
    } finally {
      setCancelRunning(false);
    }
  }, [projectId, queryClient, onClose, onChatRoute, location.search, location.pathname, navigate]);

  const handleChangeTemplate = useCallback(() => {
    setWorkflowLibraryOpen(true);
  }, []);

  const schedulePlatforms = useMemo(() => {
    const pending = workflowState?.pending_artifact as { platforms?: string[] } | undefined;
    return pending?.platforms ?? project?.platforms ?? [];
  }, [project, workflowState]);

  const scheduledAt = useMemo(() => {
    const pending = workflowState?.pending_artifact as { scheduledAt?: string } | undefined;
    return pending?.scheduledAt ?? null;
  }, [workflowState]);

  const schedulePostCount = useMemo(() => {
    const pending = workflowState?.pending_artifact as { postCount?: number } | undefined;
    return pending?.postCount ?? 1;
  }, [workflowState]);

  // No active workflow → fall back to the project-context panel (audience /
  // instructions / files / conversations) so the right rail is never just an
  // oversized empty-state icon. The user expects the same unboxed sections
  // they see on /projects/:id; the workflow library is reachable via
  // PlanContextSidebar's WorkflowSummarySection CTA.
  const showProjectContext = !templateId || !workflowState || !template;

  return (
    <div className="flex h-full flex-col bg-muted/20">
      {/* Header rhythm matches PageHeaderManager: min-h-[52px] + px-4 + py-2.
          Jobs/Rams subtraction — the prior header rendered the template
          name + "Step N of M" + status pill. ALL three duplicate signals
          that already exist elsewhere on screen:
            - Template name = breadcrumb at top of viewport
            - Step counter  = visible step rows below this header
            - Status pill   = spinner / disc shape on the active step
          Three duplicates in the same panel reads as "really, really
          messy" (verbatim user feedback). Drop them. The header keeps
          ONLY the affordances: workflow-actions menu + close button.
          When no workflow is assigned (project-context view), keep the
          project name — that's the only place it's labeled. */}
      <header className={PANEL_HEADER_SHELL}>
        {showProjectContext ? (
          <>
            <h2
              className={cn(PANEL_HEADER_TITLE, 'flex-1 tracking-tight')}
              title={project?.name ?? undefined}
            >
              {project?.name ?? (projectLoading ? 'Loading project…' : 'Project')}
            </h2>
            {/* UXA-061: with no workflow assigned, the rail falls back to the
                project-context panel — give the header an explicit way to pick
                one (the PlanContextSidebar CTA can scroll out of view). */}
            {project && (
              <Button
                variant="flat"
                size="sm"
                className="shrink-0"
                onClick={() => setWorkflowLibraryOpen(true)}
              >
                Start a workflow
                <ArrowRightIcon className="size-3.5 ml-1" weight="regular" />
              </Button>
            )}
          </>
        ) : (
          // Workflow active — header has no title. Breadcrumb owns it.
          // The step rows below own state. Header is just the action slot.
          <span className="min-w-0 flex-1" aria-hidden />
        )}
        <div className="flex items-center gap-0.5 shrink-0">
          {/* Workflow-actions menu — permanent escape hatch + change
              template option, always reachable regardless of which
              chips the agent surfaces. Hidden when the right rail
              is showing the project context (no workflow active). */}
          {!showProjectContext && template && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <CircleIconButton
                  size="sm"
                  ariaLabel="Workflow actions"
                  className={cn(MOBILE_ICON_BUTTON_XS, 'hover:bg-muted/60 hover:text-foreground')}
                >
                  <DotsThreeIcon className="size-4" weight="bold" />
                </CircleIconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]">
                <DropdownMenuItem onSelect={handleChangeTemplate} className={MENU_ITEM_MOBILE}>
                  <ArrowsClockwiseIcon className="size-4" weight="regular" />
                  Change template
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => setCancelConfirmOpen(true)}
                  className={cn(MENU_ITEM_MOBILE, 'text-destructive focus:text-destructive')}
                >
                  <XCircleIcon className="size-4" weight="regular" />
                  Cancel workflow
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <CircleIconButton
            size="sm"
            ariaLabel={variant === 'dock' ? 'Collapse panel' : 'Close'}
            className={cn(MOBILE_ICON_BUTTON_XS, 'hover:bg-muted/60 hover:text-foreground')}
            onClick={onClose}
          >
            {variant === 'dock' ? (
              <SidebarSimpleIcon className="size-3.5" weight="regular" />
            ) : (
              <XIcon className="size-3.5" weight="regular" />
            )}
          </CircleIconButton>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto" aria-busy={projectLoading || templateLoading}>
        {projectLoading || templateLoading ? (
          // Informative loading skeleton — better than a centered spinner.
          // Mirrors the cadence of the populated tracker (pill rows with a
          // disc + body line) so the layout doesn't jump when data lands.
          <div
            className="flex flex-col px-5 py-6 space-y-4"
            role="status"
            aria-label="Loading workflow"
          >
            <div className="space-y-2">
              <div className="h-3.5 w-40 rounded-md bg-muted/60 animate-pulse" />
              <div className="h-2.5 w-56 rounded-md bg-muted/40 animate-pulse" />
            </div>
            <ol className="space-y-3">
              {[0, 1, 2, 3].map((i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 size-5 shrink-0 rounded-full bg-muted/50 animate-pulse" />
                  <span
                    className="block h-3 rounded-md bg-muted/40 animate-pulse"
                    style={{ width: `${70 - i * 8}%` }}
                  />
                </li>
              ))}
            </ol>
          </div>
        ) : showProjectContext ? (
          <PlanContextSidebar
            campaign={project}
            filteredAssets={filteredAssets}
            variant="inspector"
            onOpenWorkflowLibrary={() => setWorkflowLibraryOpen(true)}
            onUploadClick={() => {
              // Drag-drop inside the sidebar handles uploads natively.
            }}
          />
        ) : (
          <div className="flex flex-col">
            {/* Mike-pattern simplification: workflows are PROMPTS, not a
                step-by-step tracker. The chat itself shows progress through
                messages. The side panel's job is just (a) "what is this
                workflow" + (b) "what's the one next thing I do". The brief
                anchor pickers are deferred — when the agent needs anchors
                it asks in chat.
                The rest of this body kept rendering the step list as a
                review surface for completed runs; we keep that, just
                without the per-row action buttons that confused users
                about what to click. */}
            {/* "Ready when you are" + Start button only renders OFF the chat
                route (i.e. on /projects/:id). On the home composer the chat itself
                carries the kickoff: the agent's welcome message asks for
                input inline, so a parallel Start CTA in the rail is a
                dual-surface violation (Subtraction) — the user sees TWO
                affordances asking for the same thing. Route-gating preserves
                the entry point for the project workbench while letting the home composer
                stay single-focus. */}
            {/* UXA-060: relax the Start gate so a not_started workflow always
                exposes a Start affordance. Off the chat route it stays the
                full "Ready when you are" block; on the chat route it collapses
                to a single compact Start button so it nudges the user without
                duplicating the agent's inline welcome copy (Subtraction). */}
            {/* UF-8: on the chat route the in-chat WorkflowKickoffSkeleton owns
                the Start CTA, so the rail no longer renders its own (the old
                centered, label-less button read as awkwardly floating + was a
                dual-surface violation). Off the chat route (/projects/:id) the
                rail keeps the full "Ready when you are" kickoff block. */}
            {workflowState.status === 'not_started' && !onChatRoute && (
              <div className="px-5 pt-6 pb-4 text-center space-y-3">
                <h3 className="text-base font-medium text-foreground">Ready when you are</h3>
                <p className={cn(ITEM_META, 'max-w-xs mx-auto')}>
                  Tap Start and Mora will walk through {steps.length} steps in the chat.
                </p>
                <Button variant="neutral" size="sm" onClick={handleStartWorkflow} className="mt-2">
                  Start workflow
                  <ArrowRightIcon className="size-3.5 ml-1" weight="regular" />
                </Button>
              </div>
            )}

            {workflowState.status === 'completed' && (
              <div className="px-5 pt-6 pb-4 text-center space-y-2">
                <h3 className="text-base font-medium text-foreground">All done</h3>
                <p className={cn(ITEM_META, 'max-w-xs mx-auto')}>
                  {steps.length} {steps.length === 1 ? 'step' : 'steps'} complete. Your assets are
                  in the media library.
                </p>
              </div>
            )}

            {/* Step list — render mode only. Per-row Approve/Regenerate
                action buttons removed; they confused users about WHERE to
                click. Active-step actions consolidate into the sticky
                footer below so there's exactly ONE primary action visible. */}
            <ol role="list" className="flex flex-col">
              {steps.map((step, idx) => {
                const renderState = resolveStepState(step, workflowState);
                const isFailed = renderState === 'failed';
                const failureReason = isFailed ? findStepFailureReason(step, workflowState) : null;
                const completedAt = findStepCompletedAt(step, workflowState);
                const stepArtifact = findStepArtifact(step, workflowState);
                const isActive = renderState === 'awaiting_approval';
                return (
                  <WorkflowStepRow
                    key={step.step_id}
                    step={step}
                    index={idx}
                    state={renderState}
                    completedAt={completedAt}
                    primaryAction={null}
                    secondaryAction={null}
                  >
                    {isActive && stepArtifact?.url && (
                      <div className="pt-2">
                        <StepArtifactPreview artifact={stepArtifact} />
                      </div>
                    )}
                    {isFailed && (
                      <div className="pt-2">
                        <WorkflowFailureBanner reason={failureReason} onRetry={handleRetry} />
                      </div>
                    )}
                  </WorkflowStepRow>
                );
              })}
            </ol>
          </div>
        )}
      </div>

      {/* Sticky footer — single primary action ALWAYS visible. The user
          never has to hunt for "what do I click next." Action shape:
            awaiting_approval → Approve / Schedule (+ Regenerate)
            failed            → Retry
            running           → Spinner + "Working…"
            completed         → "Open project" (only one) */}
      {workflowState && template && workflowState.status !== 'not_started' && (
        <footer className="shrink-0 border-t border-border/40 px-4 py-3">
          {workflowState.status === 'awaiting_approval' && (
            <div className="flex flex-col gap-2">
              {activeStepCanRegenerate && (
                <Textarea
                  rows={2}
                  value={regenFeedback}
                  onChange={(e) => setRegenFeedback(e.target.value)}
                  placeholder="Optional: what should change?"
                  disabled={isDispatching}
                  aria-label="What should change before regenerating?"
                  className="resize-none text-detail"
                />
              )}
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="flat"
                  size="sm"
                  onClick={handleRegenerate}
                  disabled={isDispatching}
                >
                  Regenerate
                </Button>
                <Button
                  variant="neutral"
                  size="sm"
                  onClick={handleApprove}
                  disabled={isDispatching}
                >
                  {activeStepSchedules ? 'Schedule' : 'Approve'}
                  <ArrowRightIcon className="size-3.5 ml-1" weight="regular" />
                </Button>
              </div>
            </div>
          )}
          {workflowState.status === 'failed' && (
            <div className="flex items-center justify-end">
              <Button variant="neutral" size="sm" onClick={handleRetry} disabled={isDispatching}>
                Retry step
                <ArrowRightIcon className="size-3.5 ml-1" weight="regular" />
              </Button>
            </div>
          )}
          {workflowState.status === 'running' && (
            <div className={cn('flex items-center gap-2', ITEM_META)}>
              <Spinner size="sm" />
              <span>
                Working on &ldquo;
                {activeStep?.action ?? `step ${currentIndex + 1}`}
                &rdquo;…
              </span>
            </div>
          )}
          {workflowState.status === 'completed' && project?.name && (
            <button
              type="button"
              onClick={goToProject}
              className={cn('group flex items-center justify-between w-full text-left', ITEM_META)}
            >
              <span>Open {project.name}</span>
              <ArrowRightIcon
                className="size-3.5 text-muted-foreground/60 group-hover:text-foreground transition-colors"
                weight="regular"
              />
            </button>
          )}
          {workflowState.status === 'completed' && isEngagementWorkflow && (
            <button
              type="button"
              onClick={() => openDock('engagement')}
              className={cn('group flex items-center justify-between w-full text-left', ITEM_META)}
            >
              <span>Open Engagement Hub to send</span>
              <ArrowRightIcon
                className="size-3.5 text-muted-foreground/60 group-hover:text-foreground transition-colors"
                weight="regular"
              />
            </button>
          )}
        </footer>
      )}

      <ScheduleConfirmDialog
        open={scheduleDialogOpen}
        onOpenChange={setScheduleDialogOpen}
        postCount={schedulePostCount}
        scheduledAt={scheduledAt}
        platforms={schedulePlatforms}
        onConfirm={handleScheduleConfirm}
      />

      {project && (
        <WorkflowLibraryDialog
          projectId={project.id}
          open={workflowLibraryOpen}
          onOpenChange={setWorkflowLibraryOpen}
        />
      )}

      <Confirm
        open={cancelConfirmOpen}
        onOpenChange={setCancelConfirmOpen}
        title="Cancel this workflow?"
        description="The workflow assignment is removed from this project. Your project, chat history, and any generated assets are kept — you can assign a new workflow later."
        confirmLabel={cancelRunning ? 'Cancelling…' : 'Cancel workflow'}
        cancelLabel="Keep workflow"
        variant="destructive"
        onConfirm={handleCancelWorkflow}
      />
    </div>
  );
};
